import API from "../api/api";
import ClientCache from "../helper/clientCache";

// In order to save requests and time and not have to deal with all the async function
// calls inside components, we use a trick to load ingame data.
// First, components that actually need ingame data will be wrapped inside a
// <WorldDataLoader> component, telling it which data is needed.
// WorldDataLoader preloads all data of the given types, then renders the actual
// component.
// Component then uses the getters to access it instantly and synchronously.
// This is quite cheap and easy, since the data is heavily cached server-side,
// so we basically just download a json file.

// This is not true for histograms and non-inactive historical data,
// as these are not cached server-side and are also used quire rarely.

const caches = {};

function buildKeyById(id) {
    return 'id:' + id;
}

function getById(type, id) {
    return caches[type].get(buildKeyById(id));
}

function getAll(type) {
    return caches[type].getAll();
}

export const WorldDataTypes = {
    'Village': 'Village',
    'Player': 'Player',
    'Kingdom': 'Kingdom',
    'Alliance': 'Alliance',
    'Cropper': 'Cropper',
    'Inactive': 'Inactive',
    'Oasis': 'Oasis',
    'Field': 'Field',
}

export function getNameProperty(type) {
    let nameProperty;
    switch (type) {
        case WorldDataTypes.Village:
        case WorldDataTypes.Player:
            nameProperty = 'name';
            break;
        case WorldDataTypes.Alliance:
            nameProperty = 'sname';
            break;
        case WorldDataTypes.Kingdom:
            nameProperty = 'tag';
            break;
        default:
            nameProperty = null;
            break;
    }
    return nameProperty;
}

export function getIdProperty(type) {
    let nameProperty;
    switch (type) {
        case WorldDataTypes.Village:
            nameProperty = 'did';
            break;
        case WorldDataTypes.Player:
            nameProperty = 'sid';
            break;
        case WorldDataTypes.Alliance:
            nameProperty = 'aid';
            break;
        case WorldDataTypes.Kingdom:
            nameProperty = 'kid';
            break;
        case WorldDataTypes.Inactive:
            nameProperty = 'did';
            break;
        case WorldDataTypes.Oasis:
            nameProperty = 'oid';
            break;
        case WorldDataTypes.Field:
            nameProperty = 'fid';
            break;
    }
    return nameProperty;
}

const isLoading = { };

async function preloadType(type) {
    if(isLoading[type]) {
        return isLoading[type];
    }
    let resolveLoadingPromise;
    isLoading[type] = new Promise((resolve) => { resolveLoadingPromise = resolve });

    const cacheUrl = await API.getWithQuery(
        'travian/worldData/' + type + '/',
        { serverUrl: SERVERINFO.url }
    );
    const data = await API.getFromCache(cacheUrl);
    const idProperty = getIdProperty(type);
    const cache = caches[type] = new ClientCache();
    data.forEach(datum => {
        cache.set(buildKeyById(datum[idProperty]), datum)
    });

    resolveLoadingPromise();
}

function isPreloaded(type) {
    return caches[type] !== undefined;
}

export function arePreloaded(types) {
    return types.reduce((isLoaded, type) => isPreloaded(type) && isLoaded, true);
}

export async function preload(types) {
    await Promise.all(
        types.map(async type => isPreloaded(type) ? Promise.resolve(null) : await preloadType(type))
    )
}

export function getVillageById(id) { return getById(WorldDataTypes.Village, id); }
export function getPlayerById(id) { return getById(WorldDataTypes.Player, id); }
export function getKingdomById(id) { return getById(WorldDataTypes.Kingdom, id); }
export function getAllianceById(id) { return getById(WorldDataTypes.Alliance, id); }
export function getOasisById(id) { return getById(WorldDataTypes.Oasis, id); }
export function getFieldById(id) { return getById(WorldDataTypes.Field, id); }
export function getInactiveById(id) { return getById(WorldDataTypes.Inactive, id); }
export function getCropperById(id) { return getById(WorldDataTypes.Cropper, id); }

export function getVillages() { return getAll(WorldDataTypes.Village); }
export function getPlayers() { return getAll(WorldDataTypes.Player); }
export function getKingdoms() { return getAll(WorldDataTypes.Kingdom); }
export function getAlliances() { return getAll(WorldDataTypes.Alliance); }
export function getOases() { return getAll(WorldDataTypes.Oasis); }
export function getFields() { return getAll(WorldDataTypes.Field); }
export function getInactives() { return getAll(WorldDataTypes.Inactive); }
export function getCroppers() { return getAll(WorldDataTypes.Cropper); }
